/**
 * The overview's reading guide and closest human categories (DESIGN UD-33). Additive: both sections only explain numbers the
 * page already shows, from the model's own data. Figures come from `figuresFor` (the model card), the calibrated index
 * (the engine's status, points and length group) and the human reference (`closestCategories`, which reuses the Channels
 * view's comparison); this file formats them and does no further arithmetic.
 */
import type { ComponentChildren } from 'preact';
import { useMemo, useState } from 'preact/hooks';
import type { CalibratedStatus } from '../../engine/ensemble/wording.ts';
import type { GlossaryKey } from '../../shared/help-types.ts';
import { GREEN_TAIL, MIN_N, RED_TAIL } from '../../shared/human-reference.ts';
import { TRANSLATION_FIGURES, figuresFor, modelCardHrefFor } from '../../shared/model-figures.ts';
import { binFor, closestCategories, listedEnough } from '../../shared/overview-reading.ts';
import type { CategoryFit, ReadingChannel } from '../../shared/overview-reading.ts';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { useHumanReference } from '../bench/HumanCompare.tsx';
import { ChannelTerm, Term } from '../components/Term.tsx';
import { fmt1, fmtInt } from '../format.ts';
import { loadReadingOpen, saveReadingOpen } from '../prefs.ts';
import { hashFor } from '../router.ts';
import { HUMAN_REF as HR } from '../strings-bench.ts';
import {
	HEADLINE_EXPLAIN as HX,
	NOTICE_EXPLAIN as NX,
	UNREACHABLE_EXPLAIN,
	ceilingBelowReference,
} from '../strings-explain.ts';
import { glossaryFor } from '../strings-glossary.ts';
import { CLOSEST, READING as R } from '../strings-reading.ts';
import { TRANSLATION as TR } from '../strings.ts';

const pct1 = (x: number): string => `${(x * 100).toFixed(1)}%`;
/** A value on the calibration's value scale, as the index shows it (100 times, one decimal). */
const idx1 = (value: number): string => (value * 100).toFixed(1);
const group = (g: string): string => g.replace('-', '–');

function Row({ id, title, children }: { id: string; title: string; children: ComponentChildren }) {
	return (
		<article class="reading-row" data-testid={`reading-${id}`}>
			<h3>{title}</h3>
			<dl class="reading-dl">{children}</dl>
		</article>
	);
}

function Item({ label, children }: { label: string; children: ComponentChildren }) {
	return (
		<>
			<dt>{label}</dt>
			<dd>{children}</dd>
		</>
	);
}

const READING_TERMS: GlossaryKey[] = [
	'status',
	'reference-point',
	'length-group',
	'human-range',
	'closest-categories',
	'model-card',
];

const CLOSEST_TERMS: GlossaryKey[] = ['closest-categories', 'human-range', 'length-group'];

const STATUS_ORDER: CalibratedStatus[] = [
	'flagged',
	'at-reference',
	'below',
	'abstain',
	'abstain-interval',
	'not-available',
];

function IndexRow({ vm }: { vm: ViewModel }) {
	const e = vm.ensemble;
	const ci = e.calibratedIndex;
	const fig = figuresFor(e.model.id);
	const R_ = R.index;
	if (!ci)
		return (
			<Row id="index" title={R_.title}>
				<Item label={R.rows.label.what}>{R_.what}</Item>
				<Item label={R.rows.label.thresholds}>{NX.uncalibrated}</Item>
				<Item label={R.rows.label.not}>{R_.not}</Item>
			</Row>
		);
	const short = ci.group ? fig.shortPoint[ci.group] : undefined;
	const bin = binFor(fig.byBin, ci.proseWords);
	const both = bin !== null && Object.values(fig.shortPoint).some((p) => bin.to <= p.maxWords);
	const unreachable = ci.group ? fig.unreachable[ci.group] : undefined;
	const statuses = STATUS_ORDER.filter((s) => !(s === 'abstain' && fig.abstentionBandEmpty));
	return (
		<Row id="index" title={R_.title}>
			<Item label={R.rows.label.what}>{R_.what}</Item>
			<Item label={R.rows.label.scale}>{R_.scale}</Item>
			<Item label={R.rows.label.thresholds}>
				<p data-testid="reading-document">
					{ci.group
						? R_.document(fmtInt(ci.proseWords), group(ci.group), ci.statusPhrase)
						: R_.noGroup(fmtInt(ci.proseWords), fmtInt(ci.floorWords))}
				</p>
				<ul>
					<li data-testid="reading-reference-point">
						{R_.referencePoint(fmt1(ci.referenceIndex))}
					</li>
					{short ? (
						<li data-testid="reading-short-point">
							{R_.shortPoint(
								short.name,
								short.threshold.toFixed(3),
								fmtInt(short.maxWords),
								idx1(short.ceiling),
								R_.shortPointRate(
									String(short.human.flagged),
									fmtInt(short.human.n),
									pct1(short.human.bound)
								),
								R_.shortPointRate(
									String(short.esl.flagged),
									fmtInt(short.esl.n),
									pct1(short.esl.bound)
								),
								R_.shortPointRate(
									String(short.speeches.flagged),
									fmtInt(short.speeches.n),
									pct1(short.speeches.bound)
								),
								ceilingBelowReference(short.ceiling, ci.referenceIndex),
								short.crossValidated
							)}
						</li>
					) : null}
					{unreachable ? (
						<li>
							{UNREACHABLE_EXPLAIN(unreachable.maxWords, unreachable.ceiling, unreachable.tStar)}
						</li>
					) : null}
					{fig.abstentionBandEmpty ? <li>{R_.noBand}</li> : null}
				</ul>
			</Item>
			<Item label={R.rows.label.statuses}>
				<ul class="reading-statuses" data-testid="reading-statuses">
					{statuses.map((s) => (
						<li key={s} data-status={s} class={ci.status === s ? 'is-current' : undefined}>
							{R_.statusList[s]}
							{ci.status === s ? <strong> ({R_.thisStatus})</strong> : null}
						</li>
					))}
				</ul>
			</Item>
			<Item label={R.rows.label.record}>
				{bin ? (
					<p data-testid="reading-record-bin">
						{R_.recordLead(
							bin.to >= 1_000_000
								? R_.rangeOpen(fmtInt(bin.from))
								: R_.rangeBetween(fmtInt(bin.from), fmtInt(bin.to - 1)),
							bin.bin,
							both ? R_.pointsBoth : R_.pointsReference
						)}{' '}
						{R_.recordHuman(String(bin.human.flagged), fmtInt(bin.human.n), pct1(bin.human.bound))};{' '}
						{bin.machine
							? R_.recordMachine(
									String(bin.machine.flagged),
									fmtInt(bin.machine.n),
									pct1(bin.machine.lower)
								)
							: R_.recordMachineUnmeasured}
						.
					</p>
				) : (
					<p>{ci.group ? R_.recordNoBin : R_.recordBelowFloor(fmtInt(ci.floorWords))}</p>
				)}
				<p>
					{R_.recordAll(
						String(fig.pooledHuman.flagged),
						fmtInt(fig.pooledHuman.n),
						pct1(fig.pooledHuman.bound)
					)}
				</p>
				<details class="reading-more">
					<summary>{R_.byCategoryTitle}</summary>
					<ul>
						{fig.strata.map((s) => (
							<li key={s.stratum}>
								{R_.byCategoryRow(
									HR.strata[s.stratum] ?? s.label,
									String(s.flagged),
									fmtInt(s.n),
									pct1(s.bound)
								)}
							</li>
						))}
					</ul>
				</details>
				<p>
					<a href={modelCardHrefFor(fig.modelId)} target="_blank" rel="noreferrer noopener">
						{R_.modelCard} {fig.modelId}
					</a>
				</p>
			</Item>
			<Item label={R.rows.label.not}>{R_.not}</Item>
		</Row>
	);
}

function IntervalRow({ vm }: { vm: ViewModel }) {
	const ci = vm.ensemble.calibratedIndex;
	if (!ci) return null;
	const T = R.interval;
	return (
		<Row id="interval" title={T.title}>
			<Item label={R.rows.label.what}>{T.what}</Item>
			<Item label={R.rows.label.scale}>{T.scale}</Item>
			<Item label={R.rows.label.thresholds}>
				{T.thresholds}
				{ci.interval ? ` ${T.group(ci.interval.gated ? 'yes' : 'no')}` : ''}
			</Item>
			<Item label={R.rows.label.not}>{T.not}</Item>
		</Row>
	);
}

function HumanRangeRow() {
	const T = R.humanRange;
	return (
		<Row id="human-range" title={T.title}>
			<Item label={R.rows.label.what}>{T.what}</Item>
			<Item label={R.rows.label.scale}>
				{T.scale(String(GREEN_TAIL), String(RED_TAIL), String(MIN_N))}
			</Item>
			<Item label={R.rows.label.record}>{HR.caveat}</Item>
			<Item label={T.see}>
				<a href={hashFor('channels')}>{T.link}</a>
			</Item>
		</Row>
	);
}

function TranslationRow({ vm }: { vm: ViewModel }) {
	const t = vm.ensemble.translation;
	const T = R.translation;
	const f = TRANSLATION_FIGURES;
	const calibrated = t.g4 === true && t.calibratedIndex !== undefined;
	return (
		<Row id="translation" title={T.title}>
			<Item label={R.rows.label.what}>{T.what}</Item>
			<Item label={R.rows.label.scale}>{T.scale}</Item>
			<Item label={R.rows.label.thresholds}>
				{T.thresholds} {TR.scope}
			</Item>
			<Item label={R.rows.label.record}>
				{calibrated
					? T.passed
					: T.record(
							String(f.machine.flagged),
							fmtInt(f.machine.n),
							pct1(f.machine.lower),
							String(f.humanTranslations.flagged),
							fmtInt(f.humanTranslations.n),
							pct1(f.humanTranslations.bound),
							String(f.humanOriginals.flagged),
							fmtInt(f.humanOriginals.n),
							pct1(f.humanOriginals.bound),
							`${Math.round(f.pairAuroc.value * 100)}%`,
							`${Math.round(f.pairAuroc.ci[0] * 100)}%`,
							`${Math.round(f.pairAuroc.ci[1] * 100)}%`,
							fmtInt(f.pairAuroc.n)
						)}
			</Item>
			<Item label={R.rows.label.not}>{T.not}</Item>
		</Row>
	);
}

/** "How to read these numbers" (UD-33): open by default, hidden or shown per browser. */
export function ReadingGuide({ vm }: { vm: ViewModel }) {
	const [open, setOpen] = useState(loadReadingOpen);
	const toggle = () => {
		setOpen(!open);
		saveReadingOpen(!open);
	};
	return (
		<section aria-labelledby="h-reading" data-testid="reading-guide">
			<div class="sec-head">
				<h2 id="h-reading">{R.title}</h2>
				<button
					type="button"
					class="btn-text"
					aria-expanded={open}
					aria-controls="reading-body"
					aria-label={`${open ? R.hide : R.show} ${R.toggleLabel}`}
					data-testid="reading-toggle"
					onClick={toggle}
				>
					{open ? R.hide : R.show}
				</button>
			</div>
			{open ? (
				<div id="reading-body" data-testid="reading-body">
					<p class="explain">{R.intro}</p>
					<IndexRow vm={vm} />
					<IntervalRow vm={vm} />
					<HumanRangeRow />
					<TranslationRow vm={vm} />
					<Row id="composite" title={R.composite.title}>
						<Item label={R.rows.label.what}>{R.composite.what}</Item>
						<Item label={R.rows.label.scale}>{R.composite.scale}</Item>
						<Item label={R.rows.label.thresholds}>{R.composite.thresholds}</Item>
						<Item label={R.rows.label.not}>{R.composite.not}</Item>
					</Row>
					<Row id="coverage" title={R.coverage.title}>
						<Item label={R.rows.label.what}>{R.coverage.what}</Item>
						<Item label={R.rows.label.scale}>{R.coverage.scale}</Item>
						<Item label={R.rows.label.thresholds}>{R.coverage.thresholds}</Item>
					</Row>
					<Row id="channels" title={R.channels.title}>
						<Item label={R.rows.label.what}>{R.channels.what}</Item>
						<Item label={R.rows.label.scale}>{R.channels.scale}</Item>
						<Item label={R.rows.label.thresholds}>{R.channels.thresholds}</Item>
						<Item label={R.rows.label.not}>{R.channels.not}</Item>
					</Row>
					<p class="caption">{HX.notLines}</p>
					<p class="caption" data-testid="reading-terms">
						{HX.termsLabel}:{' '}
						{READING_TERMS.map((k, i) => (
							<span key={k}>
								{i > 0 ? ' · ' : ''}
								<Term k={k}>{glossaryFor(k).term}</Term>
							</span>
						))}
					</p>
				</div>
			) : null}
		</section>
	);
}

function Fit({ fit, rank, names }: { fit: CategoryFit; rank: number; names: Map<string, string> }) {
	const label = HR.strata[fit.stratum] ?? fit.stratum;
	return (
		<li class="closest-row" data-testid="closest-row" data-stratum={fit.stratum}>
			<div class="closest-head">
				<strong>
					{CLOSEST.rank(String(rank))} {label}
				</strong>{' '}
				<span class="dim">{CLOSEST.documents(fmtInt(fit.documents))}</span>
			</div>
			<p class="explain">
				{CLOSEST.fits(String(fit.within), String(fit.measured))} ·{' '}
				{CLOSEST.outside(String(fit.outside), String(fit.far))} ·{' '}
				{CLOSEST.pools(fmtInt(fit.poolMin), fmtInt(fit.poolMax))}
			</p>
			{fit.signals.length > 0 ? (
				<>
					<p class="explain">
						{fit.signalsKind === 'distinguishing'
							? CLOSEST.signalsDistinguishing
							: CLOSEST.signalsNearest}
					</p>
					<ul class="closest-signals">
						{fit.signals.map((s) => (
							<li key={s.channel} data-channel={s.channel}>
								<ChannelTerm id={s.channel} name={names.get(s.channel)} />:{' '}
								{CLOSEST.signal(
									fmt1(s.score),
									fmt1(s.inCategory.range[0]),
									fmt1(s.inCategory.range[1]),
									fmt1(s.inCategory.median),
									fmt1(s.inPooled.range[0]),
									fmt1(s.inPooled.range[1]),
									s.inPooled.band !== 'within'
								)}
							</li>
						))}
					</ul>
				</>
			) : null}
		</li>
	);
}

/** "Closest human categories" (UD-33): the five categories whose channel ranges the text fits best, and the channels behind each. */
export function ClosestCategories({ vm }: { vm: ViewModel }) {
	const human = useHumanReference();
	const rows = vm.ensemble.channels;
	const result = useMemo(() => {
		if (!human.ref) return null;
		const channels: ReadingChannel[] = rows.map((r) => ({
			id: r.id,
			score: r.status === 'scored' && r.score !== undefined ? r.score : null,
		}));
		return closestCategories(human.ref, channels, vm.meta.words);
	}, [human.ref, rows, vm.meta.words]);
	const names = new Map<string, string>(rows.map((r) => [r.id, r.name]));
	const top = result?.listed.slice(0, 5) ?? [];
	return (
		<section aria-labelledby="h-closest" data-testid="closest-categories">
			<div class="sec-head">
				<h2 id="h-closest">{CLOSEST.title}</h2>
				<a href={hashFor('channels')}>{CLOSEST.openChannels}</a>
			</div>
			{result ? (
				<>
					<p class="explain">{CLOSEST.intro(String(result.scored))}</p>
					<p class="explain" data-testid="closest-caveat">
						{CLOSEST.caveat}
					</p>
					<p class="caption" data-testid="closest-terms">
						{HX.termsLabel}:{' '}
						{CLOSEST_TERMS.map((k, i) => (
							<span key={k}>
								{i > 0 ? ' · ' : ''}
								<Term k={k}>{glossaryFor(k).term}</Term>
							</span>
						))}
					</p>
					{!listedEnough(result.scored, result.applicable) ? (
						<p class="explain" data-testid="closest-few">
							{CLOSEST.fewChannels(String(result.scored), String(result.applicable))}
						</p>
					) : null}
					{top.length === 0 ? (
						<p data-testid="closest-none">{CLOSEST.none}</p>
					) : (
						<ol class="closest-list" data-testid="closest-list">
							{top.map((f, i) => (
								<Fit key={f.stratum} fit={f} rank={i + 1} names={names} />
							))}
						</ol>
					)}
					{result.unmeasured.length > 0 ? (
						<div data-testid="closest-unmeasured">
							<div class="eyebrow">{CLOSEST.unmeasuredTitle}</div>
							<p class="explain">{CLOSEST.unmeasuredBody}</p>
							<ul>
								{result.unmeasured.map((u) => (
									<li key={u.stratum} data-stratum={u.stratum}>
										{CLOSEST.unmeasuredItem(
											HR.strata[u.stratum] ?? u.stratum,
											String(u.measured),
											String(u.scored),
											fmtInt(u.poolMax)
										)}
									</li>
								))}
							</ul>
						</div>
					) : null}
				</>
			) : (
				<p class="caption" data-testid="closest-status">
					{human.error ? CLOSEST.failed : CLOSEST.loading}
				</p>
			)}
		</section>
	);
}
